import { t } from '@lingui/macro';

import { CITY_FIELDNAME, COUNTRY_FIELDNAME, POSTALCODE_FIELDNAME, STREET_FIELDNAME } from '../components/business/checkout/CheckoutFieldsAddress';
import { EMAIL_FIELDNAME } from '../components/generic/form/InputEmail';
import { PHONE_COUNTRYCODE_FIELDNAME, PHONE_FIELDNAME } from '../components/generic/form/InputPhone';
import { ROUTE_PAGE_CHECKOUT_SUCCESS } from '../constants';
import { devMessage } from '../settings/settings';
import { getFullFrontUrl } from '../utils/navigationUtil';
import { adaptPhoneForBackend } from '../utils/phoneUtil';
import { isStrNullOrEmpty } from '../utils/strUtil';
import { FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME } from './useReadAdditionalData';

// The card details never transit through us: the inputs live in stripe's own iframes
// and confirmPayment sends them straight to stripe from the browser
const useConfirmStripePayment = stripeRef => {
  const confirmStripePayment = async paymentData => {
    const { api, hasBillingAddress } = stripeRef.current;
    if (api == null) return { errorMessage: t`An error occured, please try again later.` };

    try {
      const { error, paymentIntent } = await api.stripe.confirmPayment({
        elements: api.elements,
        confirmParams: {
          return_url: getStripeReturnUrl(),
          payment_method_data: { billing_details: getStripeBillingDetails(paymentData, hasBillingAddress) },
        },
        redirect: 'if_required',
      });

      // When stripe needs a full redirect we never get here, the browser has already left
      if (error) return { errorMessage: getStripeErrorMessage(error) };

      devMessage('Stripe payment intent:', paymentIntent);
      return { errorMessage: null };
    } catch (err) {
      console.error(err);
      return { errorMessage: t`An error occured, please try again later.` };
    }
  };

  return confirmStripePayment;
};

export default useConfirmStripePayment;

// Stripe localizes these messages for us, but only the ones meant to be read by a customer
const getStripeErrorMessage = error => {
  const isCustomerError = error.type === 'card_error' || error.type === 'validation_error';
  if (isCustomerError && error.message) return error.message;

  console.error(error);
  return t`An error occured, please try again later.`;
};

// We stay inside the iframe, and we keep the current query params so that bedisplay is not lost
const getStripeReturnUrl = () => `${window.location.origin}${getFullFrontUrl(ROUTE_PAGE_CHECKOUT_SUCCESS)}${window.location.search}`;

const getStripeBillingDetails = (paymentData, hasBillingAddress) => {
  const billingDetails = {
    name: `${paymentData[FIRST_NAME_FIELDNAME]} ${paymentData[LAST_NAME_FIELDNAME]}`.trim(),
    email: paymentData[EMAIL_FIELDNAME],
    phone: adaptPhoneForBackend(paymentData[PHONE_FIELDNAME], paymentData[PHONE_COUNTRYCODE_FIELDNAME]),
  };

  if (hasBillingAddress) {
    billingDetails.address = {
      line1: paymentData[STREET_FIELDNAME],
      city: paymentData[CITY_FIELDNAME],
      postal_code: paymentData[POSTALCODE_FIELDNAME],
      country: paymentData[COUNTRY_FIELDNAME],
    };
  }

  return removeEmptyBillingDetails(billingDetails);
};

// Stripe rejects empty strings, it wants the key absent or null
const removeEmptyBillingDetails = billingDetails => {
  for (const key of Object.keys(billingDetails)) {
    if (typeof billingDetails[key] === 'object') continue;
    if (isStrNullOrEmpty(billingDetails[key])) delete billingDetails[key];
  }
  return billingDetails;
};
